/**
 * Anticipi fatture, controlli di coerenza (12/10/2026): what in the advances
 * does not agree with the movements of the bank, each with its correction.
 * The corrections exact to the cent are applied by themselves (decision of
 * 12/10/2026); the others are shown for a person to look at.
 */
import { addDays } from './dates.js';
import { invoiceNumberCore } from './advances.js';
import { formatMoney, fromCents, toCents } from './money.js';
import { textCitesNumber } from './reconciliation.js';
export const ADVANCE_CHECK_LABELS = {
    scambio: 'Fattura scambiata',
    successivo: 'Anticipo successivo',
    'incasso-mancante': 'Incasso mancante',
    'da-indicare': 'Anticipo senza fatture',
    scaduto: 'Anticipo aperto oltre la scadenza',
};
/** Days after the due date of an invoice before its open advance is pointed out (2.09). */
export const ADVANCE_OVERDUE_DAYS = 30;
function italian(date) {
    return date.split('-').reverse().join('/');
}
/**
 * The checks of the advances:
 * - «scambio»: the bank repaid an invoice that is in no advance, while an
 *   advance holds an invoice of the same customer and amount never repaid
 *   (two invoices alike, the wrong one chosen);
 * - «successivo»: a further advance («Ant. Succ.») without invoices whose
 *   amount is exactly what a repayment gave back beyond the first advance of
 *   an invoice;
 * - «incasso-mancante»: the bank repaid the advance of an invoice, so the
 *   customer paid, but the invoice is still to collect;
 * - «da-indicare» (2.09): an advance without its invoices;
 * - «scaduto» (2.09): an advance of an invoice never given back, a month
 *   after the invoice fell due. At Intesa the customer paid the bank, which
 *   kept it: nothing reaches the account, so a person says so.
 */
export function advanceChecks(input) {
    const checks = [];
    const invoiceOf = new Map(input.invoices.map((invoice) => [invoice.id, invoice]));
    const inAdvance = new Set(input.advances.flatMap((advance) => advance.invoices.map((item) => item.invoiceId)));
    // Swapped invoices: a repayment of an invoice in no advance.
    const swapped = new Set();
    for (const closure of input.unmatched) {
        if (closure.kind !== 'rimborso')
            continue;
        const core = invoiceNumberCore(closure.invoiceNumber);
        const repaid = input.invoices
            .filter((invoice) => invoiceNumberCore(invoice.number) === core &&
            invoice.date <= closure.date &&
            !inAdvance.has(invoice.id))
            .sort((a, b) => b.date.localeCompare(a.date))[0];
        if (!repaid)
            continue;
        const places = input.advances.flatMap((advance) => advance.accountCode !== closure.accountCode ||
            advance.date > closure.date ||
            advance.date < repaid.date
            ? []
            : advance.invoices
                .filter((item) => {
                const other = invoiceOf.get(item.invoiceId);
                return (!swapped.has(`${advance.movementId}|${item.invoiceId}`) &&
                    item.closures.length === 0 &&
                    toCents(item.presented) === toCents(repaid.netDue) &&
                    toCents(item.advanced) === toCents(closure.amount) &&
                    other !== undefined &&
                    (other.subjectId ?? other.counterpartyName) ===
                        (repaid.subjectId ?? repaid.counterpartyName));
            })
                .map((item) => ({ advance, item })));
        if (places.length !== 1)
            continue;
        const { advance, item } = places[0];
        swapped.add(`${advance.movementId}|${item.invoiceId}`);
        checks.push({
            kind: 'scambio',
            accountCode: advance.accountCode,
            advanceId: advance.movementId,
            reference: advance.reference,
            invoiceId: repaid.id,
            number: repaid.number,
            counterpartyName: repaid.counterpartyName,
            text: `La banca ha rimborsato la FT ${repaid.number} (${formatMoney(closure.amount)}, ${italian(closure.date)}), che non era in nessun anticipo: nell’anticipo del ${italian(advance.date)} c’era la FT ${item.number}, stesso importo, mai rimborsata. Messa la ${repaid.number} al posto della ${item.number}.`,
            fix: { kind: 'scambio', advanceId: advance.movementId, from: item.invoiceId, to: repaid.id },
            movement: null,
        });
    }
    // Further advances: what a repayment gave back beyond the first advance of an invoice.
    const excesses = input.advances.flatMap((advance) => advance.invoices.flatMap((item) => {
        const back = item.closures
            .filter((closure) => closure.kind === 'rimborso')
            .reduce((sum, closure) => sum + toCents(closure.amount), 0);
        const excess = back - toCents(item.advanced);
        const last = item.closures.filter((closure) => closure.kind === 'rimborso').pop();
        return excess > 0 && last
            ? [{ advance, item, excess, repaidOn: last.date, used: false }]
            : [];
    }));
    for (const advance of input.advances) {
        if (!advance.followUp || advance.invoices.length > 0)
            continue;
        const fits = excesses.filter((candidate) => !candidate.used &&
            candidate.advance.accountCode === advance.accountCode &&
            candidate.advance.date <= advance.date &&
            candidate.repaidOn >= advance.date &&
            candidate.excess === toCents(advance.advanced));
        if (fits.length !== 1)
            continue;
        const found = fits[0];
        found.used = true;
        checks.push({
            kind: 'successivo',
            accountCode: advance.accountCode,
            advanceId: advance.movementId,
            reference: advance.reference,
            invoiceId: found.item.invoiceId,
            number: found.item.number,
            counterpartyName: found.item.counterpartyName,
            text: `Anticipo successivo del ${italian(advance.date)} (${formatMoney(advance.advanced)}) sulla FT ${found.item.number}: il suo rimborso del ${italian(found.repaidOn)} supera di ${formatMoney(fromCents(found.excess))} il primo anticipo. Indicata la fattura.`,
            fix: {
                kind: 'successivo',
                advanceId: advance.movementId,
                invoiceId: found.item.invoiceId,
                presented: found.item.presented,
            },
            movement: null,
        });
    }
    // Repaid by the bank, so paid by the customer, and still to collect here.
    const listed = new Set();
    for (const advance of input.advances) {
        for (const item of advance.invoices) {
            const invoice = invoiceOf.get(item.invoiceId);
            const repaid = item.closures.filter((closure) => closure.kind === 'rimborso').pop();
            if (!invoice || !repaid || toCents(invoice.residual) <= 0 || listed.has(invoice.id))
                continue;
            listed.add(invoice.id);
            const payment = input.openCredits
                .filter((movement) => movement.accountCode === advance.accountCode &&
                movement.bookingDate <= repaid.date &&
                movement.bookingDate >= addDays(repaid.date, -15) &&
                (textCitesNumber(movement.description, invoice.number) ||
                    toCents(movement.amount) === toCents(invoice.residual)))
                .sort((a, b) => b.bookingDate.localeCompare(a.bookingDate))[0];
            checks.push({
                kind: 'incasso-mancante',
                accountCode: advance.accountCode,
                advanceId: advance.movementId,
                reference: advance.reference,
                invoiceId: invoice.id,
                number: invoice.number,
                counterpartyName: invoice.counterpartyName,
                text: payment
                    ? `La banca ha chiuso l’anticipo il ${italian(repaid.date)}, quindi il cliente ha pagato, ma la fattura risulta da incassare (${formatMoney(invoice.residual)}). C’è un bonifico non riconciliato del ${italian(payment.bookingDate)} di ${formatMoney(payment.amount)}.`
                    : `La banca ha chiuso l’anticipo il ${italian(repaid.date)}, quindi il cliente ha pagato, ma la fattura risulta da incassare (${formatMoney(invoice.residual)}). Il bonifico del cliente non è tra i movimenti da riconciliare: forse è già riconciliato con un’altra fattura, o non è caricato.`,
                fix: null,
                movement: payment
                    ? {
                        id: payment.id,
                        accountCode: payment.accountCode,
                        date: payment.bookingDate,
                        amount: payment.amount,
                    }
                    : null,
            });
        }
    }
    // Advances without invoices, but those a correction above just gave theirs.
    const fixed = new Set(checks.map((check) => check.advanceId));
    for (const advance of input.advances) {
        if (advance.invoices.length > 0 || fixed.has(advance.movementId))
            continue;
        checks.push({
            kind: 'da-indicare',
            accountCode: advance.accountCode,
            advanceId: advance.movementId,
            reference: advance.reference,
            invoiceId: null,
            number: null,
            counterpartyName: null,
            text: `Anticipo del ${italian(advance.date)} di ${formatMoney(advance.advanced)}${advance.presented ? ` su ${formatMoney(advance.presented)} presentati` : ''}: le fatture non sono indicate.`,
            fix: null,
            movement: null,
        });
    }
    // Never given back a month after the invoice fell due; not an invoice a correction above takes away.
    const replaced = new Set(checks.flatMap((check) => check.fix?.kind === 'scambio' ? [`${check.fix.advanceId}|${check.fix.from}`] : []));
    if (input.today) {
        for (const advance of input.advances) {
            for (const item of advance.invoices) {
                const invoice = invoiceOf.get(item.invoiceId);
                const due = invoice?.nextDue;
                if (!invoice ||
                    !due ||
                    listed.has(invoice.id) ||
                    replaced.has(`${advance.movementId}|${item.invoiceId}`) ||
                    item.closures.length > 0 ||
                    toCents(item.open) <= 0 ||
                    toCents(invoice.residual) <= 0 ||
                    addDays(due, ADVANCE_OVERDUE_DAYS) > input.today) {
                    continue;
                }
                listed.add(invoice.id);
                checks.push({
                    kind: 'scaduto',
                    accountCode: advance.accountCode,
                    advanceId: advance.movementId,
                    reference: advance.reference,
                    invoiceId: invoice.id,
                    number: invoice.number,
                    counterpartyName: invoice.counterpartyName,
                    text: `Anticipo del ${italian(advance.date)} di ${formatMoney(item.open)} ancora aperto: la fattura è scaduta il ${italian(due)}. Se il cliente ha pagato la banca, che ha trattenuto l’anticipo, segna la fattura incassata dalla banca; altrimenti manca il rimborso o l’incasso.`,
                    fix: null,
                    movement: null,
                });
            }
        }
    }
    return checks;
}
//# sourceMappingURL=advance-checks.js.map